import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button } from "react-bootstrap";
import { useUserContext } from "../Context/UserContext";
import { usePortal } from "../Portal/PortalContext";
import { useLogout } from "../Utils/useLogout";
import { Question, questionApi } from "../Questions/api";
import { apiBase, customerStatusLabel } from "../Reviews/api";
import "../Questions/questions.css";
import ReviewHelp from "../Reviews/ReviewHelp";

type CardRow = {
  question: Question;
  review: NonNullable<Question["reviews"]>[number];
};

/** The customer's email, password, saved cards, and sign-out in one place. */
export default function AccountPage() {
  const { user } = useUserContext();
  const beta = Boolean(usePortal().access?.targetedBeta);
  const { logout, signingOut } = useLogout();
  const email: string =
    user?.email || (user?.username?.includes("@") ? user.username : "");
  const [cards, setCards] = useState<CardRow[] | null>(null);
  const [cardError, setCardError] = useState("");
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(
    null
  );
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const c = new AbortController();
    // Cards are saved per review request; show the ones on running reviews.
    setCards(null);
    setCardError("");
    if (beta) {
      setCards([]);
      return () => c.abort();
    }
    // Read every page: a saved card may belong to an older running request.
    const readCards = async () => {
      const questions: Question[] = [];
      let offset: number | null = 0;
      do {
        const page: { questions: Question[]; nextOffset?: number | null } =
          await questionApi(
            `?status=all&filter=in_review${offset ? `&offset=${offset}` : ""}`,
            "GET",
            undefined,
            c.signal
          );
        if (c.signal.aborted) return { questions: [] };
        questions.push(...page.questions);
        offset = page.nextOffset ?? null;
      } while (offset !== null);
      return { questions };
    };
    readCards()
      .then(d =>
        setCards(
          d.questions.flatMap(question =>
            (question.reviews || [])
              .filter(r => r.card && r.status !== "cancelled" && !r.closed_at)
              .map(review => ({ question, review }))
          )
        )
      )
      .catch(() => {
        if (!c.signal.aborted)
          setCardError("We couldn’t load the cards for your active requests.");
      });
    return () => c.abort();
  }, [reload, beta]);

  async function sendPasswordLink() {
    setSending(true);
    setNotice(null);
    try {
      const r = await fetch(`${apiBase}/api/requestpasswordreset`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!r.ok) throw new Error();
      setNotice({
        ok: true,
        text: `We sent a link to ${email}. Use it to choose a new password.`,
      });
    } catch {
      setNotice({
        ok: false,
        text: "Could not send the link. Try again in a minute.",
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <main className="reviewed-documents rv-account-page">
      <header className="reviewed-documents-heading">
        <div>
          <h1>Account</h1>
        </div>
      </header>
      <ReviewHelp />
      <section className="rv-account-section" aria-label="Email">
        <h2>Email</h2>
        <p>
          {email ||
            "Your email is unavailable. Refresh the page to reload your account."}
        </p>
      </section>
      <section className="rv-account-section" aria-label="Password">
        <h2>Password</h2>
        {notice && (
          <Alert variant={notice.ok ? "success" : "danger"}>
            {notice.text}
          </Alert>
        )}
        <Button
          variant="outline-dark"
          disabled={!email || sending}
          onClick={sendPasswordLink}
        >
          {sending ? "Sending…" : "Email me a link to change my password"}
        </Button>
      </section>
      {beta ? (
        <section className="rv-account-section" aria-label="Beta payments">
          <h2>No payments in the test beta</h2>
          <p>
            Review fees are simulated. No card is saved or charged for a test
            review.
          </p>
        </section>
      ) : (
        <section className="rv-account-section" aria-label="Saved cards">
          <h2>Cards for active review requests</h2>
          <p className="text-secondary">
            Cards are saved for individual requests. Closed reviews are not
            listed here. Reviewed is operated by Norix Legal LLC, the merchant
            shown at checkout.
          </p>
          {cardError ? (
            <Alert variant="warning">
              {cardError}{" "}
              <Button variant="link" onClick={() => setReload(n => n + 1)}>
                Retry
              </Button>
            </Alert>
          ) : cards === null ? (
            <p role="status">Loading…</p>
          ) : cards.length ? (
            <ul className="rv-account-cards">
              {cards.map(({ question, review }) => (
                <li key={review.id}>
                  <div>
                    <strong>
                      {cardName(review.card!.brand)} •••• {review.card!.last4}
                    </strong>
                    <span>
                      For “{question.title}” · {customerStatusLabel(review)} ·
                      {review.payment_state === "paid"
                        ? "Payment received"
                        : "Charged when the agreed review starts"}
                    </span>
                  </div>
                  <Link
                    to={`/questions/${question.id}?tab=attorney&review=${review.id}`}
                  >
                    View review
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-secondary">
              No active requests with a saved card. You add a card when you
              request a review; completed reviews stay available in My
              questions.
            </p>
          )}
        </section>
      )}
      <section className="rv-account-section" aria-label="Review records">
        <h2>Your review records</h2>
        <p>
          Find agreed prices, payment status, written answers, and messages in
          each attorney review.
        </p>
        <Link to="/questions">View my questions and reviews</Link>
      </section>
      <section className="rv-account-section">
        <Button variant="link" className="p-0" onClick={() => void logout()}>
          {signingOut ? "Signing out…" : "Sign out"}
        </Button>
      </section>
    </main>
  );
}

const cardName = (brand: string) =>
  brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Card";
